'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { Locale } from '@/lib/i18n/routing';

const DEFAULTS_ES = [
  'Envío gratis en compras mayores a $1,499 MXN',
  'Φ · 9 colecciones · piezas de culto',
  'Código Áureo · en la simpleza está oculta la grandeza',
];
const DEFAULTS_EN = [
  'Free shipping on orders over $1,499 MXN',
  'Φ · 9 collections · cult pieces',
  'Golden Code · greatness lies hidden in simplicity',
];

interface TopBarProps {
  messages?: readonly string[];
  /** Segundos entre cada mensaje. */
  interval?: number;
}

/**
 * TopBar — anuncio superior rotativo con mensajes desde prop o defaults
 * locale-aware. Si el caller pasa `messages`, se usan tal cual (ya vienen
 * resueltos del server según locale). Si no, fallback a los defaults del
 * locale activo.
 */
export function TopBar({ messages, interval = 5 }: TopBarProps) {
  const locale = useLocale() as Locale;
  const fallback = useMemo(() => (locale === 'en-US' ? DEFAULTS_EN : DEFAULTS_ES), [locale]);
  const list = messages && messages.length > 0 ? messages : fallback;
  const [index, setIndex] = useState(0);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (list.length <= 1 || reduced) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % list.length);
    }, interval * 1000);
    return () => clearInterval(id);
  }, [list.length, interval, reduced]);

  // Si el locale cambia y el index excede los nuevos messages, reset
  useEffect(() => {
    if (index >= list.length) setIndex(0);
  }, [list.length, index]);

  const current = list[index] ?? list[0];
  if (!current) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="relative z-[var(--z-sticky)] border-b border-[var(--color-border)] bg-[var(--color-surface)]"
    >
      <div className="container-body flex h-9 items-center justify-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.span
            key={current}
            initial={reduced ? { opacity: 1 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 1 } : { opacity: 0, y: -8 }}
            transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
            className="type-eyebrow text-[var(--color-fg-muted)]"
          >
            {current}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}
